import Image from 'next/future/image';
import React from 'react';
import Link from 'next/link';
import { RecentlyViewedProductT } from '@lambda/apis/src/dynamoDb/types';
import Slider from '../slider/Slider';
import ReviewsStars from '../collections/reviews/ReviewsStars';
import { priceFormater } from '@/settings/index';
import { calculateSavings, getOfferUri } from '@/lib/offer';
import { defaultBreakpoints } from '@/lib/common/breakpoints';

const Placeholder = () => (
  <div
    // eslint-disable-next-line tailwindcss/no-custom-classname
    className="hide-scrollbar flex items-center gap-5 overflow-x-scroll"
  >
    {[0, 1, 2, 3, 4].map((i) => (
      <div
        key={i}
        className="h-[300px] min-w-[250px] flex-1 animate-pulse rounded-md !bg-[#E1E2E2]"
      />
    ))}
  </div>
);

export default function RecentlyViewedSlider(props: {
  title: string;
  products: RecentlyViewedProductT[];
  fetching: boolean;
  isHomepage?: boolean;
  isAccount?: boolean;
}) {
  const { title, products, isAccount } = props;

  return (
    <div>
      <h2 className="flex items-center text-lg font-semibold md:text-xl">
        {isAccount ? title : 'Pick up where you left off'}
      </h2>
      <div className="relative mt-6">
        {props.fetching ? (
          <Placeholder />
        ) : (
          <Slider breakpoints={defaultBreakpoints} speed={300}>
            {products.map((product) => {
              const percentageSaving = calculateSavings(
                product.price,
                product.comparePrice,
              );

              return (
                <Link
                  href={getOfferUri({
                    tags: product.tags,
                    handle: product.handle,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any)}
                  key={product.sku}
                  passHref
                >
                  <a className="relative flex h-full w-full flex-col justify-start rounded-md border border-gray-200 bg-white p-5">
                    {product.isCheapest && (
                      <div className="absolute left-3 top-3 flex h-5 items-center justify-center rounded-full bg-gray-300 px-2.5 text-xs">
                        Best Offer
                      </div>
                    )}
                    <div className="flex h-full w-full flex-col">
                      <div className="mt-2 flex items-center justify-center">
                        <div className="h-24 w-24">
                          <Image
                            width={100}
                            height={100}
                            alt={product.name}
                            src={product.image}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex flex-1 flex-col justify-between text-left text-sm">
                        <div>
                          <h2
                            // eslint-disable-next-line tailwindcss/no-custom-classname
                            className="two-line-ellipsis text-sm"
                          >
                            {product.name}
                          </h2>
                          <ReviewsStars
                            hideFive
                            summary={product.summary}
                            className="mt-1.5 text-xs"
                          />
                        </div>
                        <div className="mt-8 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-extrabold">
                              {priceFormater.long(product.price)}
                            </div>
                            {product.comparePrice != null &&
                              percentageSaving > 3 && (
                                <div className="text-xs text-gray-700/30">
                                  new{' '}
                                  <span className=" line-through">
                                    {priceFormater.long(product.comparePrice)}
                                  </span>
                                </div>
                              )}
                          </div>
                          {percentageSaving > 3 && (
                            <div className="flex h-6 items-center justify-center rounded-md bg-red px-2.5 text-xs text-white">
                              Save {percentageSaving}%
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </a>
                </Link>
              );
            })}
          </Slider>
        )}
      </div>
    </div>
  );
}
